import React, { createContext, useReducer, useEffect } from "react";
import { HexpertResultData } from "../types/hexpertResultTypes";
import {
  IndustryType,
  PageForms,
  FormType,
  HexpertModalProps,
  PageStateProps,
  PageData,
} from "../types/hexpertTypes";
import { fieldsToCheckInResult, formSections } from "../helpers/formSections";
import { UnitSystem } from "../helpers/unitsConversion";

export const unitSytems = [
  UnitSystem.IMPERIAL,
  UnitSystem.METRIC,
  UnitSystem.STANDARD,
];

export type THexpertAnswer = {
  [fieldId: string]: string;
};

export type HexpertState = {
  pageProps: PageStateProps | null;
  step: number | null;
  startSteps: "start" | "end" | null;
  currentForm: PageForms | null;
  startForm: boolean;
  chosenIndustry?: IndustryType; // for the additional step 6 and 7
  modal?: HexpertModalProps;
  unitSystem?: UnitSystem;
  formAnswers?: THexpertAnswer;
  showProductResult?: boolean;
  productResult?: HexpertResultData;
  loadingResult?: boolean;
  formCheckLoading?: boolean;
  submitOnNext?: boolean;
  showLastQuestions?: boolean;
  forceSteps?: number | null;
  isInitialForceModal?: boolean;
  hexpertSteps?: string[];
};

type SetInitialAnswers = {
  answers: THexpertAnswer;
  currentForm: FormType | null;
  showProductResult: boolean;
  startSteps: HexpertState["startSteps"];
};

type SetStateAction =
  | { type: "setHexpertPageProps"; payload: PageStateProps | null }
  | { type: "setCurrentForm"; payload: FormType | null }
  | { type: "setStartForm" }
  | { type: "resetStepper" }
  | { type: "setIndustry"; payload: IndustryType }
  | { type: "setUnit"; payload: UnitSystem }
  | { type: "setAnswer"; payload: THexpertAnswer }
  | { type: "removeAnswer"; payload: string }
  | { type: "loadingResult"; payload: boolean }
  | { type: "showProductResult"; payload: any }
  | { type: "setResult"; payload: HexpertResultData }
  | { type: "formCheckLoading"; payload: boolean }
  | { type: "submitOnNext"; payload: boolean }
  | { type: "showLastQuestions"; payload: boolean }
  | { type: "isInitialForceModal"; payload: boolean }
  | { type: "setInitalAnswers"; payload: SetInitialAnswers }
  | { type: "setStartSteps"; payload: HexpertState["startSteps"] }
  | { type: "setForceSteps"; payload: HexpertState["forceSteps"] }
  | { type: "setDefaultAnswers"; payload: THexpertAnswer }
  | { type: "setHexpertSteps"; payload: string[] };

type HexpertContext = {
  state: HexpertState;
  dispatch: (action: SetStateAction) => void;
};

const initialState: HexpertState = {
  pageProps: null,
  currentForm: null,
  step: 0,
  startForm: false,
  chosenIndustry: null,
  unitSystem: UnitSystem.STANDARD,
  loadingResult: false,
  productResult: null,
  formCheckLoading: false,
  submitOnNext: false,
  showLastQuestions: false,
  startSteps: null,
  forceSteps: null,
  isInitialForceModal: false,
  hexpertSteps: null,
};

export const HexpertContext = createContext<HexpertContext>({
  state: initialState,
  dispatch: () => null,
});

const HexpertReducer = (
  state: HexpertState,
  action: SetStateAction
): HexpertState => {
  switch (action.type) {
    case "setHexpertPageProps":
      return {
        ...state,
        pageProps: action.payload,
      };
    case "setHexpertSteps":
      {const getEachHexpertStep = (form: PageForms) => {
        if (form) {
          let isSubmitReached = false;
      
          const hexpertDataLayerSteps = form?.sections?.map((section) => {
            if (isSubmitReached) return null;
      
            if (section?.title || section?.label) {
              if (section?.isSubmit) {
                isSubmitReached = true;
              }
              return section?.title || section?.label;
            }
            return null;
          }).filter((step) => step !== null);
          return hexpertDataLayerSteps
        }
      }
      return {
        ...state,
        hexpertSteps: action.payload.concat(getEachHexpertStep(state?.currentForm)),
      };
    }
    case "setCurrentForm":
      return {
        ...state,
        currentForm: {
          ...state?.pageProps?.MappedForms?.find(
            (form) => form.type === action.payload
          ),
          formGuid: state?.pageProps[action.payload],
        },
      };
    case "setStartSteps":
      return {
        ...state,
        startSteps: action.payload,
      };
    case "setStartForm":
      return {
        ...state,
        startForm: true,
      };
    case "resetStepper":
      return {
        ...initialState,
        pageProps: state.pageProps,
        startForm: true,
      };
    case "setIndustry":
      return {
        ...state,
        chosenIndustry: action.payload,
      };
    case "setUnit":
      return {
        ...state,
        unitSystem: action.payload,
      };
    case "setAnswer":
      return {
        ...state,
        formAnswers: {
          ...state.formAnswers,
          ...action.payload,
        },
      };
    case "showProductResult":
      return {
        ...state,
        showProductResult: action.payload,
      };
    case "setResult":
      return {
        ...state,
        productResult: action.payload,
      };
    case "loadingResult":
      return {
        ...state,
        loadingResult: action.payload,
      };
    case "formCheckLoading":
      return {
        ...state,
        formCheckLoading: action.payload,
      };
    case "submitOnNext":
      return {
        ...state,
        submitOnNext: action.payload,
      };
    case "setForceSteps":
      return {
        ...state,
        forceSteps: action.payload,
      };
    case "showLastQuestions":
      return {
        ...state,
        showLastQuestions: action.payload,
      };
    case "isInitialForceModal":
      return {
        ...state,
        isInitialForceModal: action.payload,
      };
    case "setDefaultAnswers": {
      return {
        ...state,
        formAnswers: action.payload,
      };
    }
    case "setInitalAnswers":
      return {
        ...state,
        formAnswers: {
          ...state.formAnswers,
          ...action.payload.answers,
        },
        showProductResult: action.payload.showProductResult,
        currentForm: {
          ...state?.pageProps?.MappedForms?.find(
            (form) => form.type === action.payload.currentForm
          ),
          formGuid: state?.pageProps[action.payload.currentForm],
        },
        startSteps: action.payload.startSteps,
      };
    case "removeAnswer":
      delete state.formAnswers[action.payload];

    default:
      return { ...state };
  }
};

type Props = {
  children: JSX.Element[] | JSX.Element;
  pageProps: PageData;
};

export const HexpertContextProvider = ({ children, pageProps }: Props) => {
  const [state, dispatch] = useReducer<
    (state: HexpertState, action: SetStateAction) => HexpertState
  >(HexpertReducer, {
    ...initialState,
    pageProps: pageProps,
  });

  useEffect(() => {
    pageProps &&
      dispatch({
        type: "setHexpertPageProps",
        payload: {
          ...pageProps,
          MappedForms: formSections(pageProps?.Forms, pageProps),
          fieldsToCheckInResult: fieldsToCheckInResult(pageProps?.Forms),
        },
      });
  }, [pageProps]);
  return (
    <HexpertContext.Provider value={{ state, dispatch }}>
      {children}
    </HexpertContext.Provider>
  );
};
